<template>
  <div class="form-container">
    <h1 class="page-title">ユーザマスタ編集</h1>
    <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
      <div class="mb-3">
        <label for="id" class="form-label">ID：</label>
        {{ user.id }}
      </div>

      <div class="mb-3">
        <label for="userName" class="form-label">ユーザ名 <span class="badge text-bg-danger">必須</span></label>
        <input v-model="user.name" type="text" placeholder="ユーザ名" class="form-control" id="userName" required>
        <div v-if="errorMessages?.name" class="alert alert-danger mt-2">{{ errorMessages?.name[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="userEmail" class="form-label">メールアドレス <span class="badge text-bg-danger">必須</span></label>
        <input v-model="user.email" type="text" placeholder="メールアドレス" class="form-control" id="userEmail" required>
        <div v-if="errorMessages?.email" class="alert alert-danger mt-2">{{ errorMessages?.email[0] }}</div>
      </div>

      <EmployeeSealImageField
        v-model:image-file="employeeSealImage"
        v-model:remove-image="removeEmployeeSealImage"
        :existing-image-url="user.employee_seal_image_url || ''"
        :error-message="errorMessages?.employee_seal_image?.[0] || ''"
        input-id="employeeSealImageEdit"
        @validation-error="handleSealValidationError"
      />

      <div class="mb-3">
        <label class="form-label">システム別権限 <span class="badge text-bg-danger">必須</span></label>
        <div
          v-for="clientSystem in clientSystems"
          :key="clientSystem.key"
          class="mb-2"
        >
          <label :for="`role-${clientSystem.key}`" class="form-label">{{ clientSystem.label }}</label>
          <select
            :id="`role-${clientSystem.key}`"
            v-model="user.roles_by_system[clientSystem.key]"
            class="form-control"
            required
          >
            <option
              v-for="roleOption in roleOptions"
              :key="`${clientSystem.key}-${roleOption.value}`"
              :value="String(roleOption.value)"
            >
              {{ roleOption.label }}
            </option>
          </select>
        </div>
        <div v-if="errorMessages?.roles_by_system" class="alert alert-danger mt-2">
          {{ errorMessages?.roles_by_system[0] }}
        </div>
      </div>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-success">更新</button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { getUser, updateUser, getUserRoleCatalog } from '@/api/axios';
import EmployeeSealImageField from '@/components/shared/EmployeeSealImageField.vue';

export default {
  components: { EmployeeSealImageField },
  setup() {
    const title = ref('ユーザ編集');
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const userId = route.params.id;

    const user = ref({
      name: '',
      email: '',
      role: '',
      roles_by_system: {}
    });
    const errorMessages = ref({});
    const clientSystems = ref([]);
    const roleOptions = ref([]);
    const currentClientSystem = ref(null);
    const employeeSealImage = ref(null);
    const removeEmployeeSealImage = ref(false);

    watchEffect(() => {
      document.title = title.value;
    });

    const fetchRoleCatalog = async () => {
      const response = await getUserRoleCatalog();
      clientSystems.value = response.data.client_systems || [];
      roleOptions.value = response.data.role_options || [];
      currentClientSystem.value = response.data.current_client_system || clientSystems.value[0]?.key;
    };

    const loadUserData = async () => {
      const response = await getUser(userId);
      user.value = {
        ...response.data,
        roles_by_system: response.data.roles_by_system || {}
      };

      clientSystems.value.forEach((system) => {
        if (user.value.roles_by_system[system.key] === undefined || user.value.roles_by_system[system.key] === null) {
          user.value.roles_by_system[system.key] = String(response.data.role);
        } else {
          user.value.roles_by_system[system.key] = String(user.value.roles_by_system[system.key]);
        }
      });
    };

    const buildRolesBySystemPayload = () => {
      return clientSystems.value.reduce((acc, system) => {
        const parsedRole = Number(user.value.roles_by_system?.[system.key]);
        if (!Number.isNaN(parsedRole)) {
          acc[system.key] = parsedRole;
        }
        return acc;
      }, {});
    };

    const handleSubmit = async () => {
      try {
        const rolesBySystem = buildRolesBySystemPayload();
        const roleForCurrentSystem = rolesBySystem[currentClientSystem.value] ?? Number(user.value.role);

        await updateUser(userId, {
          ...user.value,
          role: roleForCurrentSystem,
          roles_by_system: rolesBySystem,
          employee_seal_image: employeeSealImage.value,
          remove_employee_seal_image: removeEmployeeSealImage.value
        });

        store.dispatch('setFlashMessage', { message: 'ユーザー更新完了！', type: 'success' });
        router.push({
          path: `/users/${userId}`,
          query: route.query
        });
      } catch (error) {
        errorMessages.value = error.response?.data || {};
        store.dispatch('setFlashMessage', { message: 'ユーザー更新失敗！', type: 'danger' });
      }
    };

    const handleSealValidationError = (message) => {
      const nextErrors = { ...errorMessages.value };
      if (message) nextErrors.employee_seal_image = [message];
      else delete nextErrors.employee_seal_image;
      errorMessages.value = nextErrors;
    };

    onMounted(async () => {
      await fetchRoleCatalog();
      await loadUserData();
    });

    return {
      title,
      user,
      errorMessages,
      clientSystems,
      roleOptions,
      employeeSealImage,
      removeEmployeeSealImage,
      handleSealValidationError,
      handleSubmit
    };
  }
};
</script>
